import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { MarkdownToHtmlService } from '../interfaces';
import { PLUGIN_IMPORTS_OBJ, PluginEntry, UnifiedPlugin } from '../entities';

export class UnifiedMarkdownToHtmlService implements MarkdownToHtmlService {
  private readonly logger: Logger;
  private readonly plugins: {
    core: Array<PluginEntry>;
    dangerous: Array<PluginEntry>;
    safe: Array<PluginEntry>;
  };

  constructor(logger: Logger) {
    this.logger = logger;
    this.plugins = {
      core: ['remark-parse', 'remark-gfm', ['remark-rehype', { allowDangerousHtml: true }], 'rehype-raw'],
      dangerous: [],
      safe: ['rehype-stringify'],
    };
  }

  public addDangerousPlugin(plugin: PluginEntry) {
    this.plugins.dangerous.push(plugin);
  }

  public addSafePlugin(plugin: PluginEntry) {
    this.plugins.safe.push(plugin);
  }

  public async process(markdown: string) {
    try {
      const content = markdown ?? '';
      if (!content) {
        return '';
      }
      const { unified } = await import('unified');
      const processor = unified();
      const allPluginEntries = [...this.plugins.core, ...this.plugins.dangerous, ...this.plugins.safe];

      const lazyPlugins = await this.loadPlugins(allPluginEntries);

      const pluginsToUse = lazyPlugins.map((plugin, index) => {
        const actualPlugin = plugin.default ?? plugin;
        const pluginEntry = allPluginEntries[index];

        if (Array.isArray(pluginEntry)) {
          const options = pluginEntry[1];
          return [actualPlugin, options] as [UnifiedPlugin, typeof options];
        }

        return actualPlugin;
      });

      processor.use(pluginsToUse);

      const result = await processor.process(content);
      return result.toString();
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(`Unable to process markdown due to error: ${error}`);
      return '';
    }
  }

  private async loadPlugins(plugins: Array<PluginEntry>) {
    return Promise.all(
      plugins.map((plugin) => {
        const pluginName = Array.isArray(plugin) ? plugin[0] : plugin;
        return PLUGIN_IMPORTS_OBJ[pluginName]();
      }),
    );
  }
}
